import React, { useEffect, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import Sidebar from "../Sidebar/Sidebar";
import useAuth from "../../hooks/useAuth";
import { useTranslation } from "react-i18next";

import {
  listTenantContacts,
  createTenantContact,
  updateTenantContact,
  deleteTenantContact,
} from "../../services/tenantContactService";

import ContactsToolbar from "./ContactsToolbar";
import ContactsTable from "./ContactsTable";
import ContactFormDialog from "./ContactFormDialog";

export default function TenantContactsPage() {
  const { isLoggedIn, user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listTenantContacts(); // no server filters
      setRows(Array.isArray(data) ? data : (data?.results ?? []));
    } catch (e) {
      enqueueSnackbar(e.message || t("tenantContactsPage.errors.loadFailed", "Failed to load contacts"), {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const openCreate = () => {
    setEditRow(null);
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditRow(row);
    setDialogOpen(true);
  };
  const closeDialog = () => setDialogOpen(false);

  const handleSubmit = async (form) => {
    try {
      if (editRow?.id) {
        await updateTenantContact(editRow.id, form);
        enqueueSnackbar(t("tenantContactsPage.messages.updated", "Contact updated"), { variant: "success" });
      } else {
        await createTenantContact(form);
        enqueueSnackbar(t("tenantContactsPage.messages.created", "Contact created"), { variant: "success" });
      }
      closeDialog();
      load();
    } catch (e) {
      enqueueSnackbar(e.message || t("tenantContactsPage.errors.saveFailed", "Save failed"), { variant: "error" });
    }
  };

  const handleDelete = async (row) => {
    if (!row?.id) return;

    const fullName = `${row.first_name || ""} ${row.last_name || ""}`.trim();
    const confirmText = fullName
      ? `${t("tenantContactsPage.confirm.deletePrefix", "Delete contact")} ${fullName}?`
      : `${t("tenantContactsPage.confirm.deletePrefix", "Delete contact")}?`;

    if (!window.confirm(confirmText)) return;

    setBusyId(row.id);
    try {
      await deleteTenantContact(row.id);
      enqueueSnackbar(t("tenantContactsPage.messages.deleted", "Contact deleted"), { variant: "success" });
      load();
    } catch (e) {
      enqueueSnackbar(e.message || t("tenantContactsPage.errors.deleteFailed", "Delete failed"), { variant: "error" });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Sidebar user={user} />

      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <Box sx={{ width: "100%", maxWidth: 1100 }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("tenantContactsPage.title", "Tenant Contacts")}
            </Typography>

            <ContactsToolbar
              query={query}
              onQuery={setQuery}
              typeFilter={typeFilter}
              onTypeFilter={setTypeFilter}
              onNew={openCreate}
            />
          </Paper>

          <Paper sx={{ p: 0 }}>
            <ContactsTable
              rows={rows}
              loading={loading}
              onEdit={openEdit}
              onDelete={handleDelete}
              busyId={busyId}
              query={query}
              typeFilter={typeFilter}
            />
          </Paper>
        </Box>
      </Box>

      <ContactFormDialog
        open={dialogOpen}
        onClose={closeDialog}
        onSubmit={handleSubmit}
        initial={editRow}
      />
    </>
  );
}
